/*
  Gets the questions fromm the server
  This is done when the quiz is loaded,
  it checks if there is a new version,
  otherwise serve cached question.
 */

import { QuizRequestType } from "@/interfaces/ProductApi/QuizRequestType";
import { InterimInfoType } from "@/interfaces/ProductApi/InterimInfoType";
import { AnswerType } from "@/interfaces/ProductApi/AnswerType";
import axios, { AxiosRequestConfig, AxiosResponse } from "axios";
import { Languages } from "@/entities/enums/QuizConfigEnums";
import { ICompanyResponse } from "@/interfaces/api/responses/ICompanyResponse";
import { Match } from "@/entities/domain/Match/Match";
import Quiz from "@/entities/domain/Quiz/Quiz";
import { Answer } from "@/entities/domain/Quiz/Answer";
import { Question } from "@/entities/domain/Quiz/Question";
import { AnswerForMatching } from "@/entities/api/AnswerForMatching";
import { OperationResult } from "@/entities/domain/OperationResult";

const apiPath = process.env.VUE_APP_API_SERVICE_PATH;

const QuizService = {
	assetUrl: "",
	token: "",
	companyCode: "",

	async getQuiz(
		companyCode: string,
		quizId: number | undefined = undefined
	): Promise<OperationResult<ICompanyResponse>> {
		// Get the Quiz and Company Data from API
		// Without an QuizId, the first one in the Array will be served by the API

		if(!companyCode)
			return new OperationResult<ICompanyResponse>({Success: false, ErrorMessage: "Error in QuizService, no Company Code provided"});

		try {
			this.companyCode = companyCode;

			const data = {
				code: companyCode,
				quizId: quizId,
			};

			const config: AxiosRequestConfig = {
				method: "post",
				url: apiPath + "/tool/quiz",
				headers: {
					"Content-Type": "application/json",
				},
				data: data,
			};

			// if token would be wanted but is expired, get new token (send no token in header)
			if (
				quizId &&
				this.token &&
				JSON.parse(atob(this.token.split(".")[1])).exp > Date.now() / 1000 &&
				config.headers
			) {
				config.headers.Authorization = "Bearer " + this.token;
			}

			const result = await axios(config).catch(async (err) => {
				// try again to get Quiz and token
				return await axios(config);
			});

			this.token = result.data.token;

			return new OperationResult<ICompanyResponse>({
				Success: true,
				Data: result.data,
			})
		} catch (error) {
			console.error("Fehler beim laden des Companys", { error });

			return new OperationResult({Success: false, ErrorMessage: "Error in QuizService while loading Company"});
		}
	},

	async sendAnswer(
		questionId: number,
		answerValue: number | string | null,
		answerObject: Answer | null = null,
		questionObject: Question | null = null,
		quizId?: number
	): Promise<void> {
		// Send After each answer as Info to Server
		// Not as async await for better performance

		const sendData: InterimInfoType = {
			questionId: questionId,
			answer: answerValue,
			answerObject: answerObject, // TODO: Check if this works with new Answer Model
			questionObject: questionObject,
			quizId: quizId,
		};

		const config: AxiosRequestConfig = {
			method: "post",
			url: apiPath + "/tool/event",
			headers: {
				Authorization: "Bearer " + this.token,
				"Content-Type": "application/json",
			},
			data: sendData,
		};

		await axios(config)
			.catch(async (err) => {
				// check if token is expired
				if (err.response.status === 403) {
					await this.getQuiz(this.companyCode);
					await axios(config);
				}
			});
	},

	async sendEvent(data: any, type: string, quizId?: number): Promise<void> {
		const config: AxiosRequestConfig = {
			method: "post",
			url: apiPath + "/tool/event",
			headers: {
				Authorization: "Bearer " + this.token,
				"Content-Type": "application/json",
			},
			data: { answer: data, type: type, quizId },
		};

		await axios(config).catch(async (err) => {
			console.error(err.response); // check if token is expired
			if (err.response.status === 403) {
				await this.getQuiz(this.companyCode);
				await axios(config);
			}
		});
	},

	getAssetPath(asset: string): string {
		return this.assetUrl + asset;
	},

	setAssetUrl(assetUrl: string): void {
		this.assetUrl = assetUrl;
	},

	async getMatch(answersForMatching: AnswerForMatching[], language: Languages, gtins?: string[]): Promise<Match> {
		try {
			const dataToSend: QuizRequestType = {
				answers: answersForMatching.map((el) => {
					const valueToReturn = {
						questionId: el.QuestionId,
						answer: el.AnswerValue,
						answerId: el.AnswerId,
					} as AnswerType;
					if (el.AnswerValue && el.AnswerValue.indexOf(",") > -1) {
						valueToReturn.answers = el.AnswerValue.split(",");
						valueToReturn.answer = "";
					}

					return valueToReturn;
				}),
				language: language,
				btgProducts: gtins
			};

			const data = JSON.stringify(dataToSend);

			const config: AxiosRequestConfig = {
				method: "post",
				url: apiPath + "/tool/match",
				headers: {
					Authorization: "Bearer " + this.token,
					"Content-Type": "application/json",
				},
				data: data,
			};

			let result;

			await axios(config)
				.then((res) => {
					result = res;
				})
				.catch(async (err) => {
					// Check if token is expired
					if (err.response.status === 403) {
						// When token expired, generate new one and get matching
						await this.getQuiz(this.companyCode);
						result = await axios(config);
					} else console.error(err.response);
				});

			const matchResponse = Match.createFromApiResponse(result.data); 

			return matchResponse;
		} catch (err) {
			return {} as Match;
		}
	},

	async getSavedSessionMatch(
		id: string,
		hash: string,
		getOffers = false
	): Promise<{ match: Match; companyCode: string }> {
		try {
			const result = await axios
				.post(apiPath + "/tool/session", { id: id, hash: hash })
				.catch((err) => console.error(err.response));
			// eslint-disable-next-line quotes, dot-notation
			const sessionData = result["data"];
			this.token = sessionData.token;

			const config: AxiosRequestConfig = {
				method: "post",
				url: apiPath + "/tool/match",
				headers: {
					Authorization: "Bearer " + this.token,
					"Content-Type": "application/json",
				},
			};

			if(getOffers)
				config.data = JSON.stringify({ onlyGetOffers: true});

			const matching: AxiosResponse = await axios(config);

			console.log("Matching in getSavedSessionMatch")
			console.log(Match.createFromApiResponse(matching.data))

			return {
				match: Match.createFromApiResponse(matching.data),
				companyCode: sessionData.companyCode,
			};
		} catch (error) {
			console.error(error);

			return {} as { match: Match; companyCode: string };
		}
	},

	filterQuizForLanguage(quiz: Quiz, selectedLanguage: Languages): Quiz {
		// Since de is standard, no languageModifier is needed (= empty string)
		const languageSelectedQuiz = quiz;

		const languageFilteredQuestions = quiz.Questions?.map((question) => {
			const answersToReturn = question.Answers?.map((answer) => {
				return {
					...answer,
					Text: answer["text_" + selectedLanguage] ?? answer.Text,
					TextEn: answer.Text, // needed for VS Choice TODO: Check for new modeling
				} as Answer;
			});

			return {
				...question,
				Answers: answersToReturn,
				Text: selectedLanguage === Languages.DE ? question.Text : question["text_" + selectedLanguage],
				Explanation:
					selectedLanguage === Languages.DE
						? question.Explanation
						: question["explanation_" + selectedLanguage],
				FreeTextinputMatchingTerms:
					selectedLanguage === Languages.DE
						? question.FreeTextinputMatchingTerms
						: question["free_text_input_matching_terms_" + selectedLanguage],
			} as Question;
		});

		languageSelectedQuiz.Questions = languageFilteredQuestions;

		return languageSelectedQuiz;
	},

	sendChatbotQuestionFeedback(question?: string, feedback: 1 | -1 = 1): void {
		if(!question) return;

		const config: AxiosRequestConfig = {
			method: "post",
			url: apiPath + "/tool/chat/feedback",
			headers: {
				Authorization: "Bearer " + this.token,
				"Content-Type": "application/json",
			},
			data: { feedback: feedback, question: question },
		};

		axios(config).catch(async (err) => {
			console.error(err.response); // check if token is expired
			if (err.response.status === 403) {
				await this.getQuiz(this.companyCode);
				await axios(config);
			}
		});
	},

	async trackPackage(packageNumber: string, language: string){
		const config: AxiosRequestConfig = {
			method: "get",
			url: apiPath + "/services/tracking?trackingNumber=" + packageNumber + "&language=" + language ?? 'de'  ,
			headers: {
				Authorization: "Bearer " + this.token,
				"Content-Type": "application/json",
			},
		};

		const res = await axios(config);
	
		return res.data;
	}
};

export default QuizService;
